import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/components/resource-library/resourceLibraryApi';
import { GeneratorForm } from '@/components/course-generator/GeneratorForm';
import { JobCard } from '@/components/course-generator/JobCard';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

export default function CourseGeneratorPage() {
  const [jobs, setJobs] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/course-generator/jobs`, { headers: authHeaders() });
    if (r.status === 403) return setForbidden(true);
    const d = await r.json();
    setJobs(d.jobs || []);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!jobs?.some((j) => j.status === 'running' || j.status === 'queued')) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [jobs, load]);

  const remove = async (job) => {
    if (!window.confirm('Remove this job from the list? Generated files stay in the Resource Library.')) return;
    const r = await fetch(`${API}/api/course-generator/jobs/${job.id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) { toast.success('Removed'); load(); } else toast.error('Could not remove');
  };

  if (forbidden) return <div className="p-10 text-center text-slate-600" data-testid="cg-forbidden">The AI Course Generator is only available to the resource library admin.</div>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="course-generator-page">
      <div className="rounded-2xl bg-[#1c2951] text-white p-6 flex items-start gap-4">
        <div className="rounded-xl bg-white/10 p-3"><Sparkles className="h-7 w-7 text-amber-300" /></div>
        <div>
          <h1 className="text-2xl font-bold">AI Course Generator</h1>
          <p className="text-sm text-slate-200 mt-1">Claude writes a complete Engagement Edition textbook, Activity Booklet, Answer Key, Study Guide and 2026 Planning Workbook for any Year 7–13 subject, in the same format as the Year 7 and 8 books. Output is saved as PDF + Word into the Resource Library and the interactive planner.</p>
        </div>
      </div>
      <GeneratorForm onCreated={load} />
      <div className="space-y-3">
        <h2 className="text-base md:text-lg font-semibold text-slate-800">Generation history</h2>
        {jobs === null && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>}
        {jobs?.length === 0 && <div className="text-sm text-slate-500 rounded-xl border border-dashed p-6 text-center" data-testid="cg-empty">No courses generated yet.</div>}
        {jobs?.map((j) => <JobCard key={j.id} job={j} onDelete={remove} onOpenPlanner={() => navigate(`/dashboard/planner?subject=${encodeURIComponent(j.subject)}&year=${j.year_level}`)} />)}
      </div>
    </div>
  );
}
